<template>
  <div v-if="isInitialized">
    <div v-if="hasConversations" class="alert alert-info" role="alert">
      {{ estimateMessage }}
    </div>
    <div v-else-if="!hasConversations" class="alert alert-warning" role="alert">
      No messages will be sent.
    </div>
    <exception-list v-if="hasExceptions" :items="exceptions" class="alert alert-warning" role="alert"></exception-list>
    <p>
      Send List: <strong>{{ sendListName }}</strong>
    </p>
    <el-form ref="form" :model="form" v-loading="isBusy" :rules="rules">
      <el-form-item prop="subject" label="Subject">
        <el-input
          v-model="form.subject"
          ref="subject"
        >
          <template v-slot:append>
            <email-placeholder @select="addPlaceholderToSubject">
              <el-tooltip content="Placeholders" placement="top">
                <span class="color--prpl cursor-pointer"><i class="fas fa-code"></i></span>
              </el-tooltip>
            </email-placeholder>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item prop="html_body" label="Content">
        <div class="d-inline-block w-100">
          <tinymce-editor
            ref="htmlBody"
            v-model="form.html_body"
          ></tinymce-editor>
        </div>
        <email-placeholder @select="addPlaceholderToBody">
          <el-tooltip content="Placeholders" placement="top">
            <span class="text-purple cursor-pointer"><i class="fas fa-code"></i></span>
          </el-tooltip>
        </email-placeholder>
      </el-form-item>
      <el-collapse>
        <el-collapse-item title="Schedule an email" name="schedule">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item prop="start_at" label="Start">
                <el-date-picker
                  v-model="form.start_at"
                  type="datetime"
                  :format="dateFormat"
                  :value-format="formDateFormat"
                  clearable
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="stop_at" label="Stop">
                <el-date-picker
                  v-model="form.stop_at"
                  type="datetime"
                  :format="dateFormat"
                  :value-format="formDateFormat"
                  clearable
                  disabled
                ></el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>
        </el-collapse-item>
        <el-collapse-item
          title="Follow up text message"
          name="follow-up-text"
        >
          <el-form-item label="Delay">
            <el-select
              v-model="form.follow_up_delay"
              clearable
            >
              <el-option
                v-for="{value, label} in followUpMessageProps.delayOptions"
                :key="value"
                :value="value"
                :label="label"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            prop="follow_up_message"
            class="mb-0"
          >
            <div class="d-flex flex-column">
              <label
                for="follow-up-message-control"
                class="el-form-item__label text-left line-height-15"
              >Message</label>
              <follow-up-message-control
                v-model="form.follow_up_message"
                :approved-message-list-url="followUpMessageProps.approvedMessageListUrl"
                :can-send-sms="followUpMessageProps.can.sendSms"
                :can-send-approved-sms="followUpMessageProps.can.sendApprovedSms"
                id="follow-up-message-control"
              ></follow-up-message-control>
            </div>
          </el-form-item>
        </el-collapse-item>
      </el-collapse>
      <el-form-item prop="throttle" label="Throttle">
        <el-switch
          v-model="form.throttle"
          active-color="#3d3a7f"
        ></el-switch>
      </el-form-item>
      <el-form-item>
        <button
          :disabled="isSentButtonDisabled"
          @click.prevent="save"
          class="btn btn-secondary px-4"
        >Send</button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import ValidatorMixin from "../../mixins/validator";
import PlaceholdersPicker from "../shared/placeholder/index";
import InputMixin from '../../mixins/inputs';
import data from 'emoji-mart-vue-fast/data/apple';
import {EmojiIndex, Picker} from 'emoji-mart-vue-fast';
import {isFilled} from "../../helpers";
import ExceptionList from './exception-list';
import TinymceEditor from "../tinymce-editor.vue";
import EmailPlaceholder from '../shared/placeholder/email-index.vue';
import FollowUpMessageControl from "../shared/message-control/control.vue";

export default {
  components: {
    FollowUpMessageControl,
    PlaceholdersPicker,
    Picker,
    ExceptionList,
    TinymceEditor,
    EmailPlaceholder,
  },

  mixins: [
    ValidatorMixin,
    InputMixin,
  ],

  props: {
    getFormUrl: String,
    format: {
      type: String,
      default: 'MM/DD/YY HH:mm',
    },
    excludedConversationIds: Array,
  },

  data() {
    return {
      isInitialized: false,
      storeUrl: String,
      requiredTime: Number,
      sendList: Object,
      conversationCount: null,
      exceptions: [],
      form: {
        subject: null,
        html_body: null,
        start_at: null,
        stop_at: null,
        throttle: null,
        follow_up_delay: null,
        follow_up_message: null,
      },
      rules: {
        subject: [{validator: this.fieldExternalValidator}],
        html_body: [{validator: this.fieldExternalValidator}],
        start_at: [{validator: this.fieldExternalValidator}],
        stop_at: [{validator: this.fieldExternalValidator}],
        throttle: [{validator: this.fieldExternalValidator}],
        follow_up_delay: [{validator: this.fieldExternalValidator}],
        follow_up_message: [{validator: this.fieldExternalValidator}],
      },
      errors: {},
      isBusy: false,
      now: null,
      emojiIndex: new EmojiIndex(data),
      followUpMessageProps: null,
    };
  },

  computed: {
    /**
     * @return Boolean
     */
    hasConversations() {
      return this.conversationCount > 0;
    },

    /**
     * @return {boolean}
     */
    hasExceptions() {
      return isFilled(this.exceptions);
    },

    /**
     * @return {string}
     */
    formattedDuration() {
      return moment.duration(this.requiredTime, 'seconds').humanize();
    },

    /**
     * @return String
     */
    estimateMessage() {
      return `${this.conversationCount} messages will be sent over ${this.formattedDuration}`;
    },

    /**
     * @return {String|null}
     */
    sendListName() {
      return _.get(this.sendList, 'name', null);
    },

    /**
     * @return {Boolean}
     */
    hasStartAt() {
      return !_.isNull(this.form.start_at);
    },

    /**
     * @return {string}
     */
    timezoneOffset() {
      return config('timezone.base.offset');
    },

    /**
     * @return {String}
     */
    dateFormat() {
      return config('format.datetime');
    },
    /**
     * @return {String}
     */
    formDateFormat() {
      return config('format.isoLocal');
    },

    /**
     * @return {boolean}
     */
    isSentButtonDisabled() {
      return _.get(this.form, 'follow_up_message.mode') === 'pick';
    },
  },

  methods: {
    loadForm() {
      axios.get(this.getFormUrl, {
        params: {
          except: this.excludedConversationIds || [],
        }
      })
        .then(({data: {data}}) => this.formLoadSuccess(data))
        .catch(({response: {data: {message}}}) => {
          const payload = _.assign({}, message ? {message} : {});
          this.$emit('formInitializationError', payload);
        });
    },

    /**
     * @param requiredTime
     * @param exceptions
     * @param sendList
     * @param conversationCount
     * @param storeUrl
     * @param followUpMessage
     */
    formLoadSuccess({requiredTime, exceptions, sendList, conversationCount, storeUrl, followUpMessage}) {
      this.requiredTime = requiredTime;
      this.exceptions = exceptions;
      this.sendList = sendList;
      this.conversationCount = conversationCount;
      this.storeUrl = storeUrl;
      this.followUpMessageProps = followUpMessage;
      this.isInitialized = true;
      this.$emit('formInitialized');
    },

    /**
     * @return {undefined}
     */
    refreshNow() {
      this.now = moment().utcOffset(this.timezoneOffset);
    },

    /**
     * @return {Object}
     */
    getForm() {
      return _.assign({}, this.form, {
        _method: 'POST',
        send_list_id: this.sendList.id,
        except: this.excludedConversationIds,
        follow_up_message: _.get(this.form, 'follow_up_message.text'),
      });
    },

    /**
     * @return {undefined}
     */
    save() {
      this.isBusy = true;
      axios.post(this.storeUrl, this.getForm())
          .then(this.storeSuccess)
          .catch(this.handleErrorResponse)
          .finally(() => this.isBusy = false);
    },

    /**
     * @return {undefined}
     */
    storeSuccess({data: {message = 'Successfully created!'}}) {
      this.$emit('created', {message});
    },

    /**
     * @param {Object} response
     */
    handleErrorResponse({response: {data, status}}) {
      const {errors, message} = data || {};
      if (status === 422 && errors) {
        this.errors = errors;
        this.$refs.form.validate(() => null);
        this.$message.error(message);
      } else if (message && _.isString(message)) {
        this.$message.error(message);
      }
    },

    /**
     * @param {string} value
     */
    addPlaceholderToSubject(value) {
      this.addTextInCursorPosition(
        this.$refs.subject.$refs.input,
        _.toString(this.form.subject),
        value,
        (value) => this.form.subject = value
      );
    },

    /**
     * @param value
     */
    addPlaceholderToBody(value) {
      this.$refs.htmlBody.editor.execCommand('mceInsertContent', false, value);
    },
  },

  mounted() {
    this.loadForm();
    this.refreshNow();
    this.timerId = setInterval(this.refreshNow, 5000);
  },

  beforeDestroy() {
    clearInterval(this.timerId);
  },
}
</script>
